import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, CircularProgress, Grid, Paper, Chip,
} from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { getOrderCachePanel } from "../../services/OrderCacheServices";
import { getOrderCacheDetailPanel } from "../../services/OrderCacheDetailServices";
import CustomDataGrid from "../../components/DataGrid/CustomDataGrid";
import Paginator from "../../components/paginator/Paginator";
import { getHeaders } from "./configGrid/headers";
import { formatRows } from "./configGrid/rows";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import OrderDetailModal from "./orderDetailModal";
import { ObservacionModal } from './orderObservacionesModal';
import { AuthContext } from '../../context/authContext';
import { handleOpenPdf } from '../../components/pdf/exportPdf';
import { formatHeaderPDF } from "./configPDF/headerPDF";
import { columns } from "./configPDF/columnsPDF";
import { formatRowsPDF } from "./configPDF/rowsPDF";
import ComprobantePDF from "../../components/Imprimir/ComprobantePDF";
import { ContentPasteGoOutlined } from '@mui/icons-material';

const OrderCache = () => {
  const { clienteSelected, empresa } = useContext(AuthContext);
  const [pedidos, setPedidos] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [dateFromFilter, setDateFromFilter] = useState(null);
  const [dateUntilFilter, setDateUntilFilter] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [numPagina, setNumPagina] = useState(1);
  const [filas, setFilas] = useState(10);
  const [detallePedido, setDetallePedido] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalObservacionOpen, setModalObservacionOpen] = useState(false);
  const [observacionSeleccionada, setObservacionSeleccionada] = useState('');
  const [pedidoNro, setPedidoNro] = useState(null);
  const [user, setUser] = useState(null);
  const [headerPDF, setHeaderPDF] = useState(null);
  const [columnsPDF, setColumnsPDF] = useState([]);  // ✅ typo corregido
  const [rowsPDF, setRowsPDF] = useState([]);
  const [pdfReady, setPdfReady] = useState(false);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (u) setUser(u);
  }, []);

  const rolId = user?.rol_id;
  const clienteDelUsuario = user?.id;

  useEffect(() => {
    if (rolId === 1 && clienteSelected) setClienteSeleccionado(clienteSelected.id);
    if (rolId === 2 && clienteDelUsuario) setClienteSeleccionado(clienteDelUsuario);
  }, [user, clienteSelected]);

  useEffect(() => {
    const cargarPedidos = async () => {
      if (!clienteSeleccionado) return;
      setIsLoading(true);
      try {
        const res = await getOrderCachePanel(
          clienteSeleccionado, "",
          dateFromFilter ? dayjs(dateFromFilter).format("YYYY-MM-DD") : "",
          dateUntilFilter ? dayjs(dateUntilFilter).format("YYYY-MM-DD") : "",
          numPagina, filas
        );
        setPedidos(res.datos || []);
        setTotalItems(res.pagination?.totalItems || 0);
      } catch (err) {
        console.error("Error al cargar pedidos:", err);
      } finally {
        setIsLoading(false);
      }
    };
    cargarPedidos();
  }, [clienteSeleccionado, dateFromFilter, dateUntilFilter, numPagina, filas]);

  const handleOpenDetallePedido = async (pedidoId) => {
    try {
      const res = await getOrderCacheDetailPanel(pedidoId, null, 1, 100);
      setDetallePedido(res.orderCacheDetails || []);
      setModalOpen(true);
    } catch (error) {
      console.error("Error al cargar detalles:", error);
    }
  };

  const handleOpenObservacion = (observacion, pedidoId) => {
    setObservacionSeleccionada(observacion);
    setPedidoNro(pedidoId);
    setModalObservacionOpen(true);
  };

  const handleCreatePDF = async (pedido) => {
    try {
      setHeaderPDF(null);
      setColumnsPDF([]);   // ✅ typo corregido
      setRowsPDF([]);
      setPdfReady(false);

      // ✅ Faltaba el await — sin esto detalle siempre era una Promise, nunca los datos
      const data = await getOrderCacheDetailPanel(pedido.id, null, 1, 99);
      const detalle = data?.orderCacheDetails || [];

      setHeaderPDF(formatHeaderPDF(pedido));
      setColumnsPDF(columns);  // ✅ typo corregido
      setRowsPDF(formatRowsPDF(detalle));
      setPdfReady(true);

    } catch (err) {
      console.error("Error al generar PDF:", err);
    } finally {
      // ✅ Si hay error, el pdfReady queda en false correctamente
      // (solo se setea true si todo salió bien, arriba del catch)
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} locale="es">
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBagOutlinedIcon sx={{ color: 'var(--accent)', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                Pedidos Pendientes
              </Typography>
              <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Pedidos en proceso de tu cuenta
              </Typography>
            </Box>
          </Box>
          {totalItems > 0 && (
            <Chip
              label={`${totalItems} pedido${totalItems !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.72rem',
                backgroundColor: 'var(--accent-soft)', color: 'var(--accent)',
                border: '1px solid var(--accent)',
              }}
            />
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: 'var(--accent)' }} />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Cargando pedidos...</Typography>
          </Box>
        ) : pedidos.length === 0 ? (
          <Paper elevation={0} sx={{
            p: 4, textAlign: 'center', borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border)', backgroundColor: 'var(--bg-subtle)',
          }}>
            <ShoppingBagOutlinedIcon sx={{ fontSize: 48, color: 'var(--text-muted)', opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', color: 'var(--text-secondary)', mb: 0.5 }}>
              No hay pedidos pendientes
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Tus pedidos en proceso aparecerán acá
            </Typography>
          </Paper>
        ) : (
          <>
            <CustomDataGrid
              rows={formatRows(pedidos)}
              columns={getHeaders(handleOpenObservacion, handleOpenDetallePedido, handleCreatePDF)}
              enableSelectionClick={false}
            />

            <Box sx={{
              mt: 2, p: 1.5,
              backgroundColor: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}>
              <Paginator
                numPagina={numPagina}
                setNumPagina={setNumPagina}
                totalPages={Math.ceil(totalItems / filas)}
                filas={filas}
                setFilas={setFilas}
                totalDataItems={totalItems}
              />
            </Box>

            <OrderDetailModal
              open={modalOpen}
              onClose={() => { setModalOpen(false); setDetallePedido([]); }}
              pedidoDetalles={detallePedido}
            />
            <ObservacionModal
              open={modalObservacionOpen}
              onClose={() => { setModalObservacionOpen(false); setObservacionSeleccionada(''); }}
              observacion={observacionSeleccionada}
              pedidoNro={pedidoNro}
            />
            <ComprobantePDF
              header={headerPDF}
              columns={columnsPDF}   
              rows={rowsPDF}
              titlePdf="Pedido"
              empresa={empresa}
              trigger={pdfReady}
              onFinish={() => setPdfReady(false)}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default OrderCache;